import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Button, Col, Form, Row, Table, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SelectSearch, { fuzzySearch } from "react-select-search";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getAllUserDetails, adminGetAllUsers } from "../actions/userActions";
import useIsMobile from "../utils/useIsMobile";
import FormField from "../components/FormField";
import NoKeyboardInput from "../components/NoKeyboardInput";
import SegmentedControl from "../components/SegmentedControl";
import LoaderButton from "../components/LoaderButton";

const regexNum = /^\d+$/;
const phoneNum = /^(\+91[-\s]?|91[-\s]?|0)?([6-9]\d{9})$/;
const regexText = /^(\w+\s?)*\s*$/;

const subName = [
  "Mr.",
  "Mrs.",
  "Miss.",
  "Dr/Mr.",
  "Dr/Mrs.",
  "Dr/Miss.",
  "Baby.",
  "Master.",
  "B/O",
  "Others.",
];

const format_rupees = (val) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(val);

const Booking = () => {
  const isMobile = useIsMobile();
  const history = useHistory();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userLogin.userInfo);


  const { users: adminUsers } = useSelector((state) => state.adminUsers || { users: [] });
  const isAdmin = userInfo && userInfo.role === "admin";
  const isLab = userInfo && userInfo.role === "lab";

  // Patient and booking details
  const [billDate, setBillDate] = useState(new Date());
  const [patientSubName, setPatientSubName] = useState("");
  const [patientName, setPatientName] = useState("");
  const [refDoc, setRefDoc] = useState("");
  const [ptID, setPTID] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [paymentType, setPaymentType] = useState("Cash");
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [validEmail, setValidEmail] = useState(true);
  const [ageYear, setAgeYear] = useState("");
  const [ageMonth, setAgeMonth] = useState("");
  const [company, setCompany] = useState(userInfo.company || "");
  const [assignedUser, setAssignedUser] = useState(userInfo.userId || "");

  // Billing and test details
  const [less, setLess] = useState(0);
  const [advance, setAdvance] = useState(0);
  const [billingJson, setBillingJson] = useState([{ description: "", amount: "" }]);
  const [testOptions, setTestOptions] = useState([]);
  const [testsList, setTestsList] = useState([]);
  const [stateEdit, setStateEdit] = useState(false);
  const [bookingId, setBookingId] = useState("");

  // UI and error states
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobilePop, setMobilePop] = useState(false);
  const [oldRecords, setOldRecords] = useState([]);

  const oldSearchValueRef = useRef("");
  const oldTestOptionsRef = useRef([]);

  // Derived values
  const total = useMemo(
    () =>
      billingJson.reduce(
        (sum, item) => sum + (parseInt(item.amount, 10) || 0),
        0
      ),
    [billingJson]
  );
  const netAmount = useMemo(() => total - (parseInt(less, 10) || 0), [total, less]);
  const balancePayable = useMemo(
    () => netAmount - (parseInt(advance, 10) || 0),
    [netAmount, advance]
  );

  // Fetch all users and set column span
  useEffect(() => {
    dispatch(getAllUserDetails());

    if (isAdmin || isLab) dispatch(adminGetAllUsers());
  }, [dispatch, isAdmin, isLab]);

  // Fetch booking data if editing
  useEffect(() => {
    if (window.location.search && window.location.search.includes("id")) {
      const id = window.location.search.split("=")[1];
      setBookingId(id);
      setStateEdit(true);
      getDatafromInvoice(id);
    } else {
      setStateEdit(false);
      setBookingId("");
    }
    // eslint-disable-next-line
  }, [window.location.search]);



  // Fetch booking data by invoice ID
  const getDatafromInvoice = useCallback(
    async (invId) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      };
      try {
        const res = await axios.get(`/api/v1/booking/view/${invId}`, config);
        if (res.data.data) {
          const invData = res.data.data;
          const patName = invData.patient_name.includes(".")
            ? invData.patient_name.split(".")
            : ["", invData.patient_name];
          setPatientSubName(invData.patient_name.includes(".") ? patName[0] + "." : "");
          setPatientName(invData.patient_name.includes(".") ? patName[1].trim() : invData.patient_name);
          setBillDate(new Date(invData.booking_date));
          setAge(invData.patient_age);
          setGender(invData.patient_gender);
          setRefDoc(invData.reference_doctor);
          setMobileNumber(invData.patient_mobile);
          setPTID(invData.pt_id);
          setEmail(invData.patient_email);
          setPaymentType(invData.payment_type);
          setAdvance(invData.advance);
          setLess(invData.discounts);
          setCompany(invData.company);
          let tempItems = JSON.parse(invData.items);
          setBillingJson(tempItems);
          setTestOptions(tempItems.map(item => ({ name: item.description, value: item.description })));

          if (isAdmin) {
            setCompany(invData.company || "");
            setAssignedUser(invData.createdBy || "");
          }
          const fullage = invData.patient_age.includes(".") ? invData.patient_age.split(".") : null;
          if (fullage) {
            setAgeYear(fullage[0]);
            setAgeMonth(fullage[1]);
          } else {
            setAgeYear(invData.patient_age);
          }
        } else {
          alert("No data found");
          history.push("/bookings");
        }
      } catch {
        alert("No data found");
        history.push("/bookings");
      }
    },
    [userInfo.token, history, isAdmin]
  );

  // Search for old records by mobile number
  const searchMobileData = useCallback(
    async (mobile) => {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      };
      try {
        const res = await axios.post(
          `/api/v1/bill/checkMobileNumber`,
          { mobile_number: mobile },
          config
        );
        if (res.data.data && res.data.data.length > 0) {
          setOldRecords(res.data.data);
          setMobilePop(true);
        }
      } catch { }
    },
    [userInfo.token]
  );

  // Billing description search (debounced)
  const searchBillingDescription = useCallback(
    async (value, defaultOptions = []) => {
      if (!value || value.length <= 2) {
        return defaultOptions;
      }
      if (value === oldSearchValueRef.current) {
        return oldTestOptionsRef.current;
      }
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      };
      try {
        const res = await axios.get(`/api/v1/tests/list/${value}`, config);
        oldSearchValueRef.current = value;
        if (res.data.data) {
          setTestsList(res.data.data);
          const mapped = res.data.data
            .filter((o1) => o1.test_code)
            .map((o1) => ({
              name: o1.test_code + " - " + o1.test_name,
              value: o1.test_code + " - " + o1.test_name,
            }));
          setTestOptions(mapped);
          oldTestOptionsRef.current = mapped;
          return mapped;
        }
      } catch { }
      return defaultOptions;
    },
    [userInfo.token]
  );

  // Change billing description and update amount
  const changeBillingDescription = useCallback(
    (value, index) => {
      let tempValue = value.split(" - ");
      let testItem = testsList.find(({ test_code }) => test_code === tempValue[0]);
      setBillingJson((prev) => {
        const arrObj = [...prev];
        arrObj[index].description = value;
        arrObj[index].amount = testItem ? testItem.test_price : 0;
        return arrObj;
      });
    },
    [testsList]
  );

  // Change billing amount
  const handleChangeAmount = useCallback(
    (value, index) => {
      if (!isNaN(value)) {
        setBillingJson((prev) => {
          const arrObj = [...prev];
          arrObj[index].amount = value !== "" ? +value : 0;
          return arrObj;
        });
      }
    },
    []
  );

  // Add new billing line
  const addNewLine = useCallback(() => {
    setBillingJson((prev) => [...prev, { description: "", amount: "" }]);
  }, []);

  // Delete billing line
  const deleteLine = useCallback(
    (index) => {
      setBillingJson((prev) => prev.filter((_, i) => i !== index));
    },
    []
  );

  // Change less amount
  const changeLessAmount = useCallback(
    (e) => {
      const val = e.target.value;
      if (val !== "" && val <= total && regexNum.test(val)) setLess(+val);
      else if (val === "") setLess(0);
    },
    [total]
  );

  // Change advance amount
  const changeAdvanceAmount = useCallback(
    (e) => {
      const val = e.target.value;
      if (val !== "" && val <= netAmount && regexNum.test(val)) setAdvance(+val);
      else if (val === "") setAdvance(0);
    },
    [netAmount]
  );

  // Filter test list for SelectSearch
  const filterTestList = useCallback(
    (test, val) => {
      test = Array.isArray(test) ? test : [];
      let check = test.find(({ name }) => name === val);
      return !check && val ? [...test, { name: val, value: val }] : test;
    },
    []
  );

  // Update details from old record
  const updateOldDetails = useCallback(
    (invData) => {
      const patName = invData.patient_name.includes(".")
        ? invData.patient_name.split(".")
        : ["", invData.patient_name];
      setPatientSubName(invData.patient_name.includes(".") ? patName[0] + "." : "");
      setPatientName(invData.patient_name.includes(".") ? patName[1].trim() : invData.patient_name);
      setAge(invData.patient_age);
      setGender(invData.patient_gender);
      setRefDoc(invData.reference_doctor);
      setMobileNumber(invData.patient_mobile);
      setEmail(invData.patient_email);
      setMobilePop(false);
      const fullage = invData.patient_age.includes(".") ? invData.patient_age.split(".") : null;
      if (fullage) {
        setAgeYear(fullage[0]);
        setAgeMonth(fullage[1]);
      } else {
        setAgeYear(invData.patient_age);
      }
    },
    []
  );

  // Update patient sub name and gender
  const updatePatientSubName = useCallback(
    (e) => {
      const val = e.target.value;
      setPatientSubName(val);
      if (["Mr.", "Dr/Mr."].includes(val)) setGender("Male");
      else if (["Mrs.", "Miss.", "Dr/Mrs.", "Dr/Miss."].includes(val)) setGender("Female");
      else setGender("");
    },
    []
  );

  // Submit handler
  const handleSubmit = useCallback(
    async (redirect = true) => {
      if (patientSubName && patientName && billDate && gender && mobileNumber && validEmail) {
        setIsSubmitting(true);
        let data = {
          booking_date: billDate,
          patient_name: patientSubName + " " + patientName,
          patient_age: age,
          patient_gender: gender,
          patient_mobile: mobileNumber,
          patient_email: email,
          paymentType,
          reference_doctor: refDoc,
          pt_id: ptID,
          items: billingJson,
          discount: less,
          advance: advance,
          net_amount: netAmount,
          company,
          ...(assignedUser ? { assignedUser } : {}),
        };
        if (stateEdit && bookingId) data = { ...data, id: bookingId };
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userInfo.token}`,
          },
        };
        let endpoint = stateEdit ? "update" : "create";
        try {
          const res = await axios.post(`/api/v1/booking/${endpoint}`, data, config);
          if (res.status === 200 && redirect) history.push("/bookings");
        } catch { } finally {
          setIsSubmitting(false);
        }
      } else {
        setIsError(true);
        alert("Please enter all mandatory fields");
      }
    },
    [
      patientSubName,
      patientName,
      billDate,
      gender,
      mobileNumber,
      age,
      email,
      paymentType,
      refDoc,
      ptID,
      billingJson,
      less,
      advance,
      netAmount,
      company,
      stateEdit,
      bookingId,
      userInfo.token,
      history,
      validEmail,
      assignedUser
    ]
  );

  // Move to TRF
  const moveToTrf = useCallback(
    async () => {
      await handleSubmit(false);
      history.push(`/billing?id=${bookingId}`);
    },
    [handleSubmit, history, bookingId]
  );

  // Input handlers
  const handleMobileChange = useCallback(
    (e) => {
      const rawValue = e.target.value;
      const cleanedValue = rawValue.replaceAll(/[^\d+\s]/g, "").trim();
      setMobileNumber(cleanedValue);
      const match = cleanedValue.match(phoneNum);
      if (match) {
        const tenDigitNumber = match[2];
        setMobileNumber(cleanedValue);
        if (tenDigitNumber.length === 10) {
          searchMobileData(tenDigitNumber);
        }
      }
    },
    [searchMobileData]
  );

  const handlePatientNameChange = useCallback(
    (e) => {
      !e.target.value && setPatientName("");
      if (regexText.test(e.target.value)) setPatientName(e.target.value);
    },
    []
  );

  // Render
  return (
    <div className="billing-screen pb-5">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end mb-4">
        <div className={isMobile ? "mb-3" : ""}>
          <h3 className="font-weight-bold grey-900 mb-1" style={{
            fontSize: 'clamp(1rem, 4vw, 1.4rem)', letterSpacing: ' - 0.5px'
          }}>
            {stateEdit ? "Edit Booking" : "Test Booking"}
          </h3>
          <p className="text-muted mb-0 small uppercase font-weight-bold letter-spacing-1">
            <i className={`fa ${stateEdit ? 'fa-pencil-square text-warning' : 'fa-calendar-plus-o text-primary'} mr-2`}></i>
            {stateEdit ? `Updating patient record #${bookingId}` : "Create new test requisition for patient"}
          </p>
        </div>
        <div className="d-flex flex-row flex-md-column justify-content-between align-items-end">
          {stateEdit && (
            <Button
              variant="outline-primary"
              size="sm"
              className="font-weight-bold border-2 mb-md-2 py-2 px-3"
              onClick={() => history.push("/booking")}
              style={{ borderRadius: '8px', fontSize: '0.85rem' }}
            >
              <i className="fa fa-plus-circle mr-1"></i> New Booking
            </Button>
          )}
        </div>
      </div>

      {/* Admin: User Selector */}
      {
        isAdmin && (
          <div className="billing-form-card custom-card-boxshadow mb-4 p-4" style={{ borderRadius: '20px' }}>
            <h5 className="billing-section-title mb-3 font-weight-bold d-flex align-items-center">
              <span className="section-number-icon mr-3"><i className="fa fa-user"></i></span>
              Select User
            </h5>
            <Row>
              <Col xs={12} md={6}>
                <FormField label="Assign to User" mandatory error={isAdmin && !assignedUser}>
                  <Form.Control
                    as="select"
                    className="bg-light border-0"
                    value={assignedUser}
                    onChange={(e) => {
                      const selVal = e.target.value;
                      setAssignedUser(selVal);
                      const tUser = adminUsers.find(u => String(u.user_id) === String(selVal));
                      if (tUser) setCompany(tUser.company);
                      else setCompany("");
                    }}
                  >
                    <option value="">-- Select a User --</option>
                    {adminUsers && adminUsers.filter(u => u.role !== 'admin').map((u) => (
                      <option key={u.id} value={u.user_id}>{u.user_name}</option>
                    ))}
                  </Form.Control>
                </FormField>
              </Col>
            </Row>
          </div>
        )
      }

      {/* Section 1: Patient Information */}
      <div className="billing-form-card custom-card-boxshadow mb-4 p-4" style={{ borderRadius: '20px' }}>
        <h5 className="billing-section-title mb-4 font-weight-bold d-flex align-items-center">
          <span className="section-number-icon mr-3">1</span>
          Patient Information
        </h5>
        <Row>
          <Col xs={12} sm={6} md={3}>
            <FormField label="Mobile Number" mandatory error={isError && !mobileNumber}>
              <Form.Control
                maxLength={13}
                className="bg-light border-0"
                value={mobileNumber}
                onChange={handleMobileChange}
                type="tel" placeholder="Ex: 9876543210"
              />
            </FormField>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <FormField label="Patient Name" mandatory error={isError && (!patientSubName || !patientName)}>
              <div className="d-flex" style={{ gap: '6px' }}>
                <Form.Control
                  className="bg-light border-0"
                  style={{ width: '85px' }}
                  as="select" value={patientSubName} onChange={(e) => updatePatientSubName(e)}
                >
                  <option hidden>Title</option>
                  {subName.map((o1, index) => <option value={o1} key={index}>{o1}</option>)}
                </Form.Control>
                <Form.Control
                  className="bg-light border-0 flex-grow-1"
                  value={patientName}
                  onChange={handlePatientNameChange}
                  type="text" placeholder="Full Name"
                />
              </div>
            </FormField>
          </Col>
          <Col xs={12} sm={6} md={2}>
            <FormField label="Age (Y/M)" mandatory error={isError && !age}>
              <div className="d-flex" style={{ gap: '2px' }}>
                <Form.Control
                  maxLength={2} className="bg-light border-0 text-center px-1"
                  style={{ height: '42px' }}
                  value={ageYear} onChange={(e) => {
                    if (!e.target.value || regexNum.test(e.target.value)) {
                      setAgeYear(e.target.value); setAge(e.target.value + (ageMonth ? "." + ageMonth : ""));
                    }
                  }} placeholder="Y"
                />
                <Form.Control
                  maxLength={2} className="bg-light border-0 text-center px-1"
                  style={{ height: '42px' }}
                  value={ageMonth} onChange={(e) => {
                    if (!e.target.value || regexNum.test(e.target.value)) {
                      setAgeMonth(e.target.value); setAge((ageYear || "0") + "." + e.target.value);
                    }
                  }} placeholder="M"
                />
              </div>
            </FormField>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <FormField label="Gender" mandatory error={isError && !gender}>
              <SegmentedControl
                options={["Male", "Female", "Other"]}
                value={gender}
                onChange={setGender}
              />
            </FormField>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={6} md={3}>
            <FormField label="Date & Time">
              <DatePicker
                placeholderText="Select Date & Time"
                className="form-control bg-light border-0"
                selected={billDate}
                onChange={(date) => setBillDate(date)}
                minDate={new Date()}
                showTimeSelect
                timeIntervals={15}
                timeCaption="Time"
                portalId="root-portal"
                popperPlacement="bottom-start"
                popperModifiers={[{ name: "preventOverflow", options: { boundary: "viewport" } }]}
                dateFormat="dd/MM/yyyy h:mm aa"
                withPortal={isMobile}
                customInput={isMobile ? <NoKeyboardInput className="form-control bg-light border-0" /> : undefined}
              />
            </FormField>
          </Col>
          <Col md={3}>
            <FormField label="Reference Doctor">
              <Form.Control className="bg-light border-0" value={refDoc} onChange={(e) => setRefDoc(e.target.value)} type="text" placeholder="Dr. Name" />
            </FormField>
          </Col>
          <Col md={3}>
            <FormField label="Email Address" error={!validEmail && "Invalid email address"}>
              <Form.Control
                className="bg-light border-0"
                value={email ?? ""} onChange={(e) => { setEmail(e.target.value); setValidEmail(true); }}
                onBlur={() => { if (email && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)) setValidEmail(false); }}
                type="email" placeholder="patient@email.com"
              />
            </FormField>
          </Col>
          <Col md={3}>
            <FormField label="Payment Mode">
              <SegmentedControl
                options={["Cash", "Card", "UPI"]}
                value={paymentType}
                onChange={setPaymentType}
              />
            </FormField>
          </Col>
        </Row>
      </div>

      {/* Section 2: Investigations */}
      <div className="billing-form-card custom-card-boxshadow mb-4">
        <h5 className="billing-section-title mb-4 font-weight-bold">2. Investigation Details</h5>
        <div className="investigations-container py-1">
          {/* Desktop Table: Hidden on Mobile & Tablets */}
          <div className="d-none d-md-block table-responsive border rounded-lg mb-4">
            <Table hover size="sm" className="mb-0 border-0">
              <thead className="bg-light">
                <tr>
                  <th className="text-center py-3 border-0" width="60">#</th>
                  <th className="py-3 border-0">Name of the Test</th>
                  <th className="py-3 border-0" width="160">Amount</th>
                  <th className="text-center py-3 border-0" width="100">Actions</th>
                </tr>
              </thead>
              <tbody>
                {billingJson.map((val, i) => (
                  <tr key={i} className="test-row-hover">
                    <td className="text-center align-middle border-bottom font-weight-bold">{i + 1}</td>
                    <td className="p-0 border-bottom">
                      <div className="select-search-premium-wrapper">
                        <SelectSearch
                          filterOptions={fuzzySearch} search
                          options={filterTestList(testOptions, val.description)}
                          value={val.description} placeholder="Search investigations..."
                          getOptions={searchBillingDescription} onChange={(e) => changeBillingDescription(e, i)}
                        />
                      </div>
                    </td>
                    <td className="p-0 border-bottom">
                      <Form.Control
                        type="number" className="border-0 bg-transparent font-weight-bold py-3 text-right pr-4"
                        value={val.amount} onChange={(e) => handleChangeAmount(e.target.value, i)}
                      />
                    </td>
                    <td className="text-center align-middle border-bottom">
                      <div className="d-flex justify-content-center align-items-center" style={{ gap: '12px' }}>
                        {billingJson.length === i + 1 && (
                          <Button variant="link" className="p-0 text-success" onClick={addNewLine} disabled={val.description === "" || val.amount === 0}>
                            <i className="fa fa-plus-circle fa-lg"></i>
                          </Button>
                        )}
                        {billingJson.length > 1 && (
                          <Button variant="link" className="p-0 text-danger" onClick={() => deleteLine(i)}>
                            <i className="fa fa-times-circle-o fa-lg"></i>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile Dense Layout: Compact and Clean (Visible < 768px) */}
          <div className="d-block d-md-none">
            {billingJson.map((val, i) => (
              <div key={i} className="investigation-row-dense">
                <div className="d-flex align-items-center">
                  <span className="item-index-small mr-2">{i + 1}</span>
                  <div className="flex-grow-1 select-search-premium-wrapper">
                    <SelectSearch
                      filterOptions={fuzzySearch} search
                      options={filterTestList(testOptions, val.description)}
                      value={val.description} placeholder="Search investigations..."
                      getOptions={searchBillingDescription} onChange={(e) => changeBillingDescription(e, i)}
                    />
                  </div>
                  {billingJson.length > 1 && (
                    <Button variant="link" className="p-0 text-danger ml-2" onClick={() => deleteLine(i)}>
                      <i className="fa fa-times-circle-o fa-lg"></i>
                    </Button>
                  )}
                </div>

                <div className="amount-input-dense mt-1">
                  <span>₹</span>
                  <Form.Control
                    type="number"
                    className="border-0 bg-transparent font-weight-bold p-0"
                    value={val.amount}
                    onChange={(e) => handleChangeAmount(e.target.value, i)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            ))}

            <div className="mobile-add-btn-container mb-3">
              <Button
                variant="outline-primary"
                className="btn-add-test-mobile py-2"
                onClick={addNewLine}
                disabled={billingJson[billingJson.length - 1].description === "" || billingJson[billingJson.length - 1].amount === 0}
              >
                <i className="fa fa-plus"></i> Add Test Line
              </Button>
            </div>
          </div>
        </div>

        <Row>
          <Col lg={7}>
            {isLab && (
              <FormField label="Assigned To">
                <Form.Control
                  className="bg-light border-0"
                  as="select"
                  value={assignedUser}
                  onChange={(e) => setAssignedUser(e.target.value)}
                >
                  <option value="">Select User</option>
                  {adminUsers && adminUsers
                    .filter(u => String(u.company) === String(userInfo.company) && u.is_active && u.role !== 'admin')
                    .map((u) => (
                      <option key={u.id} value={u.user_id}>{u.user_name}</option>
                    ))
                  }
                </Form.Control>
              </FormField>
            )}
            <FormField label="Special Note / Remarks">
              <Form.Control as="textarea" rows={4} className="bg-light border-0" value={ptID} onChange={(e) => setPTID(e.target.value)} placeholder="Enter any specific notes..." />
            </FormField>
          </Col>
          <Col lg={5}>
            <div className="totals-card shadow-sm mt-2">
              <div className="totals-row">
                <span className="totals-label text-muted">Subtotal</span>
                <span className="totals-value">{format_rupees(total)}</span>
              </div>
              <div className="totals-row">
                <span className="totals-label text-danger">Discount (-)</span>
                <div className="d-flex align-items-center">
                  <span className="mr-1 text-danger font-weight-bold">₹</span>
                  <input className="text-right font-weight-bold border-0 bg-transparent text-danger p-0" style={{ width: '80px', outline: 'none' }} type="number" value={less} onChange={changeLessAmount} />
                </div>
              </div>
              <div className="totals-row">
                <span className="totals-label">Net Total</span>
                <span className="totals-value font-weight-bold">{format_rupees(netAmount)}</span>
              </div>
              <div className="totals-row">
                <span className="totals-label text-success">Advance Paid (-)</span>
                <div className="d-flex align-items-center">
                  <span className="mr-1 text-success font-weight-bold">₹</span>
                  <input className="text-right font-weight-bold border-0 bg-transparent text-success p-0" style={{ width: '80px', outline: 'none' }} type="number" value={advance} onChange={changeAdvanceAmount} />
                </div>
              </div>
              <div className="totals-row mt-3 pt-3 border-top border-dark">
                <span className="totals-label text-dark font-weight-bold" style={{ fontSize: '1.1rem' }}>BALANCE PAYABLE</span>
                <span className="totals-value payable" style={{ fontSize: '1.6rem' }}>{format_rupees(balancePayable)}</span>
              </div>
            </div>
          </Col>
        </Row>

        <hr className="my-4 border-light" />

        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-2" style={{ gap: '1.2rem' }}>
          <Button
            variant="light"
            onClick={() => history.push("/bookings")}
            className={`btn-action-premium text-secondary font-weight-bold ${isMobile ? 'w-100' : ''}`}
          >
            Cancel
          </Button>

          <div className={`d-flex flex-column flex-md-row align-items-center ${isMobile ? 'w-100' : ''}`} style={{ gap: '1.2rem' }}>
            {stateEdit && (
              <Button onClick={moveToTrf} variant="primary" className={`btn-action-premium ${isMobile ? 'w-100' : ''}`}>
                <i className="fa fa-exchange mr-2"></i> Move to Billing (TRF)
              </Button>
            )}
            <LoaderButton
              isLoading={isSubmitting}
              disabled={billingJson.length === 1 && (billingJson[0].description === "" || billingJson[0].amount === "" || billingJson[0].amount === 0 || billingJson[0].amount === "0")}
              onClick={() => handleSubmit()}
              className={`btn-action-premium btn-generate-trf ${isMobile ? 'w-100' : ''}`}
              style={{ minWidth: isMobile ? '100%' : '280px' }}
            >
              <span>{stateEdit ? "Update Booking" : "Confirm Booking"}</span>
              <i className="fa fa-arrow-right ml-2"></i>
            </LoaderButton>
          </div>
        </div>
      </div>

      <Modal className="mt-5" show={mobilePop} onHide={() => setMobilePop(false)}>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="font-weight-bold">Existing Patient Records</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="small font-weight-bold text-muted text-uppercase bg-light">
                <tr>
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3">Age</th>
                  <th className="py-3">Gender</th>
                </tr>
              </thead>
              <tbody>
                {oldRecords.map((o1, index) => (
                  <tr key={index} onClick={() => updateOldDetails(o1)} style={{ cursor: "pointer" }} className="patient-row-hover">
                    <td className="py-3 px-4 font-weight-bold text-primary">{o1.patient_name}</td>
                    <td className="py-3">{o1.patient_age}</td>
                    <td className="py-3">{o1.patient_gender}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    </div >
  );
};

export default Booking;
